import React, { memo, useMemo } from 'react';
import { Star, Youtube, Globe, Wand2, X } from 'lucide-react';
import { BaseLanguageSelector, type Language, type QuickAction } from './BaseLanguageSelector';
import { getAllNLLBLanguages, type NLLBLanguage } from '@/utils/languageMapping';
import { getAllYouTubeCodes } from '@/constants/youtubeLanguages';
import { useTranslation } from '@/hooks/useTranslation';

// Special code for auto-detect option
const AUTO_DETECT_CODE = 'auto';

// Top 20 languages ordered by number of speakers worldwide
const TOP_20_LANGUAGE_CODES = [
  'en',
  'zh',
  'hi',
  'es',
  'fr',
  'ar',
  'bn',
  'pt',
  'ru',
  'ja',
  'de',
  'ko',
  'vi',
  'te',
  'mr',
  'ta',
  'tr',
  'it',
  'ur',
  'th',
];

export interface NLLBLanguageSelectorProps {
  /** Selected language codes */
  values: string[];
  /** Callback when selection changes */
  onMultiChange: (languages: string[]) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Maximum number of selections (undefined = unlimited, 1 = single select) */
  maxSelection?: number;
  /** Source language for filtering (optional) */
  sourceLanguage?: string;
  /** Show quick action buttons (Top 20, YouTube All, All, Clear). Default: true */
  showQuickActions?: boolean;
  /** Placeholder text override */
  placeholder?: string;
  /** Show auto-detect option (for source language selection). Default: false */
  showAutoDetect?: boolean;
  /** Info about detected language (code, name, confidence) */
  detectedLanguageInfo?: {
    code: string;
    name: string;
    confidence: number;
  } | null;
}

/**
 * NLLBLanguageSelector - Specialized language selector for NLLB-200 languages
 *
 * Features all 146+ NLLB languages with:
 * - Quick actions (Top 20, YouTube All, All 146, Clear)
 * - Auto-detect option for source language
 * - Region grouping
 * - Search functionality
 */
export const NLLBLanguageSelector = memo(function NLLBLanguageSelector({
  values = [],
  onMultiChange,
  disabled = false,
  className = '',
  maxSelection,
  showQuickActions = true,
  placeholder,
  showAutoDetect = false,
  detectedLanguageInfo,
}: NLLBLanguageSelectorProps) {
  const { t } = useTranslation();

  // Defensive check: ensure values is always an array
  const safeValues = Array.isArray(values) ? values : [];

  // Load NLLB languages
  const NLLB_LANGUAGES = useMemo(() => getAllNLLBLanguages(), []);
  const YOUTUBE_ALL_LANGUAGE_CODES = useMemo(() => getAllYouTubeCodes(), []);

  // Convert to base Language format
  const languages: Language[] = useMemo(
    () =>
      NLLB_LANGUAGES.map((lang: NLLBLanguage) => ({
        code: lang.code,
        name: lang.name,
        nativeName: lang.nativeName,
        flag: lang.flag,
        region: lang.region,
        nllb_code: lang.nllb_code,
      })),
    [NLLB_LANGUAGES]
  );

  // Quick actions configuration
  const quickActions: QuickAction[] = useMemo(
    () => [
      {
        id: 'top20',
        label: t('languageSelector.quickActions.selectTop20Short') || 'Top 20',
        description: t('languageSelector.quickActions.coverage70Short') || '70% coverage',
        icon: <Star className="w-3 h-3" />,
        codes: TOP_20_LANGUAGE_CODES,
        className:
          'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/20',
      },
      {
        id: 'youtube',
        label:
          t('languageSelector.quickActions.selectYouTubeAll', {
            count: YOUTUBE_ALL_LANGUAGE_CODES.length,
          }) || `YouTube ${YOUTUBE_ALL_LANGUAGE_CODES.length}`,
        description: t('languageSelector.quickActions.youtubeLabel') || 'YouTube supported',
        icon: <Youtube className="w-3 h-3" />,
        codes: YOUTUBE_ALL_LANGUAGE_CODES,
        className: 'bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20',
      },
      {
        id: 'all',
        label:
          t('languageSelector.quickActions.selectAllShort', { count: languages.length }) ||
          `All ${languages.length}`,
        description: t('languageSelector.quickActions.nllbLabel') || 'NLLB-200',
        icon: <Globe className="w-3 h-3" />,
        codes: languages.map((l) => l.code),
        className:
          'bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20',
      },
    ],
    [t, YOUTUBE_ALL_LANGUAGE_CODES, languages]
  );

  // Check if auto-detect is selected (explicit 'auto' value)
  const isAutoDetectSelected = safeValues.includes(AUTO_DETECT_CODE);

  // Check if no language is selected (empty state - allows choosing specific language)
  const isEmptySelection =
    safeValues.length === 0 || (safeValues.length === 1 && safeValues[0] === '');

  // Handle auto-detect toggle (checkbox behavior)
  const handleAutoDetectToggle = () => {
    if (disabled) return;
    if (isAutoDetectSelected) {
      // Uncheck - clear to allow manual selection
      onMultiChange(['']);
    } else {
      // Check - enable auto-detect
      onMultiChange([AUTO_DETECT_CODE]);
    }
  };

  // Custom render for auto-detect option display (as checkbox)
  const renderAutoDetectCheckbox = () => {
    if (!showAutoDetect) return null;

    return (
      <div className="mt-2">
        <label
          className={`w-full px-3 py-3 flex items-center justify-between cursor-pointer rounded-lg border transition-colors ${
            isAutoDetectSelected
              ? 'bg-purple-900/20 border-purple-500/30'
              : 'border-white/10 hover:bg-purple-900/10'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={isAutoDetectSelected}
              onChange={handleAutoDetectToggle}
              disabled={disabled}
              className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
            />
            <Wand2 className="w-5 h-5 text-purple-600" />
            <div className="text-left">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('languageSelector.autoDetect.label') || 'Auto-detect'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {t('languageSelector.autoDetect.description') ||
                  'Automatically detect source language from text'}
              </div>
            </div>
          </div>
          <span className="text-xs text-purple-600 font-medium uppercase">
            {t('languageSelector.autoDetect.badge') || 'AI'}
          </span>
        </label>
      </div>
    );
  };

  // Custom placeholder when auto-detect is selected
  const getPlaceholder = () => {
    if (isAutoDetectSelected && detectedLanguageInfo) {
      return undefined; // Will show auto-detect badge instead
    }
    return (
      placeholder ||
      t('languageSelector.providers.nllb.placeholder') ||
      `Select languages (${languages.length} available)`
    );
  };

  // Filter out auto-detect and empty strings from values for the base selector
  const baseValues = safeValues.filter((v) => v !== AUTO_DETECT_CODE && v !== '');

  // Handle change - if auto-detect was previously selected, clear it when selecting regular languages
  const handleChange = (codes: string[]) => {
    if (isAutoDetectSelected && codes.length > 0) {
      // User is selecting a regular language after auto-detect
      onMultiChange(codes);
    } else {
      onMultiChange(codes);
    }
  };

  // Handle clearing auto-detect selection - set to empty to allow manual language choice
  const handleClearAutoDetect = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    // Set empty string to indicate "no selection" - user can then pick a specific language
    // This is different from [] which would re-trigger auto-detect selection
    onMultiChange(['']); // Empty string signals "choose manually"
  };

  // Custom render for selected display when auto-detect is selected
  const renderAutoDetectSelected = () => {
    if (!isAutoDetectSelected) return null;

    return (
      <div className="flex items-center flex-wrap gap-1 mt-2 mb-2">
        <span className="inline-flex items-center px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-xs rounded-md">
          <Wand2 className="w-3 h-3 mr-1" />
          {t('languageSelector.autoDetect.label') || 'Auto-detect'}
          {detectedLanguageInfo && (
            <span className="ml-1 text-purple-600 dark:text-purple-400">
              ({detectedLanguageInfo.name} - {Math.round(detectedLanguageInfo.confidence * 100)}%)
            </span>
          )}
          <button
            type="button"
            onClick={handleClearAutoDetect}
            disabled={disabled}
            className="ml-2 p-0.5 rounded-full hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors"
            title={t('languageSelector.autoDetect.clear') || 'Select specific language'}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      </div>
    );
  };

  return (
    <div className={className}>
      {/* Regular language selector - shown above auto-detect, hidden when auto-detect is selected */}
      {!isAutoDetectSelected && (
        <BaseLanguageSelector
          languages={languages}
          values={baseValues}
          onChange={handleChange}
          disabled={disabled}
          maxSelection={maxSelection}
          showQuickActions={showQuickActions && maxSelection !== 1}
          quickActions={quickActions}
          placeholder={
            isEmptySelection
              ? t('languageSelector.selectLanguage') || 'Select a language...'
              : getPlaceholder()
          }
          searchPlaceholder={
            t('languageSelector.providers.nllb.searchPlaceholder', { count: languages.length }) ||
            `Search ${languages.length} languages...`
          }
          footerDescription={
            t('languageSelector.providers.nllb.fullDescription', { count: languages.length }) ||
            `NLLB-200 - ${languages.length} languages supported`
          }
          topLanguageCodes={TOP_20_LANGUAGE_CODES}
          topLanguagesLabel="Top 20 Most Spoken"
          testId="subtitle-language-selector-nllb"
        />
      )}

      {/* Auto-detect selected display with clear button */}
      {isAutoDetectSelected && renderAutoDetectSelected()}

      {/* Auto-detect option - rendered below the selector */}
      {showAutoDetect && renderAutoDetectCheckbox()}
    </div>
  );
});

export default NLLBLanguageSelector;
